import { EmployeeAvatarWithTooltip } from "@/components/employee/EmployeeAvatarWithTooltip.tsx";
import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { RecognitionGiftSentModal } from "@/pages/settings/recognitions/widgets/RecognitionGiftSentModal.tsx";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useRecognitionGift, useRecognitionGiftPreview } from "@/queries/use-recognitions.query.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { ApiRecognition } from "@/types/recognitions/recognition.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import {
  Rule,
  RuleApplySignType,
  RuleConditionType,
  RuleShort,
  RuleGroupShort,
} from "@/types/rule.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Box,
  Input,
  LinkButtonTo,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const defaultRecipientGroup = {
  condition_type: RuleConditionType.all_active_employees,
  sign: RuleApplySignType.include,
  condition_ids: [],
  value_ids: [],
  condition_entities: [],
  value_entities: [],
} satisfies Rule;

function toRuleShort(rule: RuleShort): RuleShort {
  return {
    condition_type: rule.condition_type,
    sign: rule.sign,
    condition_ids: rule.condition_ids,
    value_ids: rule.value_ids,
    date_condition: rule.date_condition,
  };
}

export interface RecognitionGiftModalProps extends ModalProps {
  recognition: ApiRecognition;
}

export function RecognitionGiftModal({ recognition, ...props }: RecognitionGiftModalProps) {
  const { t } = useTranslation();
  const { openModal, close } = useModals();
  const { id } = props;

  const recipientConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.recognitionGiftRecipients
  );

  const giftSchema = z
    .object({
      comment: z.string(),
      recipient: z.discriminatedUnion("mode", [
        z.object({ mode: z.literal("single"), employee: z.custom<SelectItem>().nullable() }),
        z.object({ mode: z.literal("multi"), groups: ruleGroupsSchema() }),
      ]),
    })
    .refine(
      (data) =>
        data.recipient.mode === "single"
          ? data.recipient.employee != null
          : data.recipient.groups.some((group) => group.rules.length > 0),
      { message: t("recognition:gift_recipient_required"), path: ["recipient"] }
    );

  type GiftSchema = z.infer<typeof giftSchema>;

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<GiftSchema>({
    mode: "onSubmit",
    resolver: zodResolver(giftSchema),
    defaultValues: { comment: "", recipient: { mode: "single", employee: null } },
  });

  const recipient = useWatch({ control, name: "recipient" });
  const isMultiple = recipient.mode === "multi";
  const groups = recipient.mode === "multi" ? recipient.groups : [];

  const employee = useEmployeeSearchInline(!isMultiple);

  const recipientRuleGroups = useMemo<RuleGroupShort[]>(() => {
    if (recipient.mode === "multi")
      return recipient.groups.map((group) => ({ rules: group.rules.map(toRuleShort) }));
    return recipient.employee != null
      ? [
          {
            rules: [
              {
                condition_type: RuleConditionType.employee,
                sign: RuleApplySignType.include,
                condition_ids: [recipient.employee.id],
                value_ids: [],
              },
            ],
          },
        ]
      : [];
  }, [recipient]);

  const preview = useRecognitionGiftPreview(
    recipientRuleGroups,
    isMultiple && groups.some((group) => group.rules.length > 0)
  );
  const recipientsCount = preview.data?.recipients_count ?? 0;

  const setGroups = (next: Extract<GiftSchema["recipient"], { mode: "multi" }>["groups"]) => {
    setValue("recipient", { mode: "multi", groups: next }, { shouldValidate: isSubmitted });
  };

  const switchToMultiple = () =>
    setGroups(groups.length > 0 ? groups : [{ rules: [defaultRecipientGroup] }]);
  const switchToSingle = () => {
    setValue("recipient", { mode: "single", employee: null }, { shouldValidate: isSubmitted });
  };

  const recognitionGift = useRecognitionGift(control);

  const onSubmit = handleSubmit(async (data) => {
    if (
      data.recipient.mode === "multi" &&
      (preview.isFetching || preview.isError || !preview.data || recipientsCount === 0)
    )
      return;
    try {
      await recognitionGift.mutateAsync({
        recognition_id: recognition.id,
        recipient_rule_groups: recipientRuleGroups.filter((group) => group.rules.length > 0),
        comment: data.comment.trim() || null,
      });
      close(id);
      openModal(RecognitionGiftSentModal, {
        recognition,
        recipientCount: data.recipient.mode === "multi" ? recipientsCount : 1,
        recipientName:
          data.recipient.mode === "single" ? data.recipient.employee?.title : undefined,
      });
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t("recognition:gift_modal_title")}
      withCloser
      layout={"stretch"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            className={"w-full"}
            isLoading={recognitionGift.isPending}
            disabled={
              isMultiple &&
              (preview.isFetching || preview.isError || !preview.data || recipientsCount === 0)
            }
            onClick={onSubmit}
          >
            {t("recognition:gift_send")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"lg"}>
          <Stack gap={"xs"}>
            <Title
              size={"xs"}
              paddingBottom={true}
              header={t("recognition:gift_recipient_label")}
            />

            {isMultiple ? (
              <Stack gap={"sm"}>
                <RuleGroupsEditor
                  control={control}
                  setValue={setValue}
                  fieldName="recipient.groups"
                  place={RuleRegistryPlace.recognitionGiftRecipients}
                  conditionTypes={recipientConditionTypes.conditionTypes}
                  variant="list"
                />

                <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                  <Text className={"text-secondary"} size={"xs"}>
                    {t("recognition:gift_return_to")}
                  </Text>
                  <LinkButtonTo
                    variant={"secondary"}
                    size={"sm"}
                    className={"underline text-secondary"}
                    onClick={switchToSingle}
                  >
                    {t("recognition:gift_single_recipient")}
                  </LinkButtonTo>
                </Stack>

                {preview.isError && (
                  <Stack gap="xs">
                    <Text className="text-danger">{t("core:rule_group_preview_error")}</Text>
                    <Button variant="secondary" onClick={() => void preview.refetch()}>
                      {t("common:retry")}
                    </Button>
                  </Stack>
                )}

                {groups.length > 0 &&
                  !preview.isFetching &&
                  preview.data &&
                  recipientsCount > 0 && (
                    <Box className={"mt-2 rounded-xs border border-dark/5 bg-dark/5 px-4 py-3"}>
                      <Stack gap={"sm"}>
                        <Text className={"text-title"}>
                          {t("recognition:gift_recipients_count", { count: recipientsCount })}
                        </Text>
                        {preview.data.recipients.length > 0 && (
                          <EmployeeAvatarWithTooltip
                            employees={preview.data.recipients}
                            size={"md"}
                            maxVisible={6}
                            totalCount={recipientsCount}
                          />
                        )}
                      </Stack>
                    </Box>
                  )}
              </Stack>
            ) : (
              <>
                <Select
                  emptyTitle={t("recognition:gift_choose_recipient")}
                  required={true}
                  error={errors.recipient?.message}
                  searchable={true}
                  showAvatar={true}
                  showCaption={true}
                  loadOptionsOnOpen={true}
                  options={
                    recipient.mode === "single" && recipient.employee
                      ? [recipient.employee]
                      : undefined
                  }
                  onSearchOptions={employee.search}
                  searchOptions={employee.options}
                  value={recipient.mode === "single" ? (recipient.employee?.id ?? null) : null}
                  onChange={(_value, entity) => {
                    setValue(
                      "recipient",
                      { mode: "single", employee: entity ?? null },
                      { shouldValidate: isSubmitted }
                    );
                  }}
                />
                <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                  <Text className={"text-secondary"} size={"xs"}>
                    {t("recognition:gift_send_to")}
                  </Text>
                  <LinkButtonTo
                    variant={"secondary"}
                    size={"sm"}
                    className={"underline text-secondary"}
                    onClick={switchToMultiple}
                  >
                    {t("recognition:gift_multiple_recipients")}
                  </LinkButtonTo>
                </Stack>
              </>
            )}

            {isMultiple && errors.recipient?.message && (
              <Text className={"text-danger"} size={"xs"}>
                {errors.recipient.message}
              </Text>
            )}
          </Stack>

          <Stack gap={"sm"}>
            <RecognitionBadge template={recognition} size={"lg"} />
            <Title
              size={"lg"}
              paddingTop={true}
              paddingBottom={true}
              header={recognition.title}
              caption={recognition.description}
            />
          </Stack>

          <Stack gap={"xs"}>
            <Title size={"xs"} header={t("recognition:gift_comment_label")} />
            <Input
              type={"text"}
              label={t("recognition:gift_comment_placeholder")}
              {...register("comment")}
              error={errors.comment?.message}
            />
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
